import { useQuery, UseQueryResult, Query } from '@tanstack/react-query';
import { getAgentsStatus as getAgentsStatusAPI } from 'src/common/Scraper';
import { AgentsStatusResponse } from '../types/agents-status.type';

interface UseGetAgentsStatusOptions {
  /** Whether the query should be enabled. Defaults to true. */
  enabled?: boolean;
  /**
   * Polling interval in milliseconds, or a callback that receives the query
   * and returns an interval (or false to stop polling).
   * Pass `false` or omit to disable polling.
   */
  refetchInterval?: number | false | ((query: Query<AgentsStatusResponse, Error>) => number | false | undefined);
}

/**
 * React Query hook to fetch all Orbit AI agent statuses by siteId.
 *
 * Usage:
 * ```ts
 * const { data, isLoading } = useGetAgentsStatus(siteId, {
 *   refetchInterval: hasProcessingAgent ? 5000 : false,
 * });
 * ```
 *
 * @param siteId  - The site ID to check agent statuses for
 * @param options - Optional query configuration (enabled, refetchInterval)
 */
export const useGetAgentsStatus = (
  siteId: string,
  options: UseGetAgentsStatusOptions = {}
): UseQueryResult<AgentsStatusResponse, Error> => {
  const { enabled = true, refetchInterval = false } = options;

  return useQuery<AgentsStatusResponse, Error>({
    queryKey: ['agentsStatus', siteId],
    queryFn: () => getAgentsStatusAPI(siteId),
    enabled: !!siteId && enabled,
    refetchInterval,
  });
};

export default useGetAgentsStatus;
